import type {
  DataResponse,
  NsdcKitsResponse,
  TrackerFdfcDispatchResult,
  PaginatedResponse,
  TrackerFdfcDetail,
  TrackerFdfcEntry,
  TrackerFdfcImportResult,
  TrackerFdfcListQuery,
  TrackerFdfcCallLogInput,
  TrackerFdfcSurveyInput,
  TrackerFdfcAddressInput,
} from '@tracker/shared';
import { api } from '@/lib/api';
import { downloadXlsx } from '@/lib/download';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const trackerFdfcService = {
  async list(query: Partial<TrackerFdfcListQuery> = {}): Promise<PaginatedResponse<TrackerFdfcEntry>> {
    const res = await api.get<PaginatedResponse<TrackerFdfcEntry>>('/tracker-fdfc', { params: query });
    return res.data;
  },

  async courseLevels(): Promise<string[]> {
    const res = await api.get<DataResponse<string[]>>('/tracker-fdfc/course-levels');
    return res.data.data;
  },

  async get(id: string): Promise<TrackerFdfcDetail> {
    const res = await api.get<DataResponse<TrackerFdfcDetail>>(`/tracker-fdfc/${id}`);
    return res.data.data;
  },

  /** Sends the .xlsx as the raw request body; the backend parses and imports it. */
  async importXlsx(file: File): Promise<TrackerFdfcImportResult> {
    const res = await api.post<DataResponse<TrackerFdfcImportResult>>('/tracker-fdfc/import', file, {
      headers: { 'Content-Type': XLSX_MIME },
      params: { file_name: file.name },
      // Up to 5,000 rows against a remote DB can take longer than the 15 s default.
      timeout: 120_000,
    });
    return res.data.data;
  },

  /** Kit orders the main NSDC is accepting orders for (fetched by our backend). */
  async nsdcKits(): Promise<NsdcKitsResponse> {
    const res = await api.get<DataResponse<NsdcKitsResponse>>('/tracker-fdfc/nsdc/kits', {
      timeout: 45_000,
    });
    return res.data.data;
  },

  /** Sends each entry to NSDC as one kit order. Sequential and paced server-side. */
  async dispatch(ids: string[], kitCode: string): Promise<TrackerFdfcDispatchResult> {
    const res = await api.post<DataResponse<TrackerFdfcDispatchResult>>(
      '/tracker-fdfc/dispatch',
      { ids, kit_code: kitCode },
      // A batch of 50 is paced to stay inside NSDC's rate limit (~40 s).
      { timeout: 180_000 },
    );
    return res.data.data;
  },

  /** One more kit order for an entry that already has one. A real order at NSDC. */
  async dispatchAgain(id: string, kitCode: string): Promise<{ order_code: string }> {
    const res = await api.post<DataResponse<{ order_code: string }>>(
      `/tracker-fdfc/${id}/dispatch-again`,
      { kit_code: kitCode },
      { timeout: 60_000 },
    );
    return res.data.data;
  },

  async refreshNsdc(id: string): Promise<TrackerFdfcDetail> {
    const res = await api.post<DataResponse<TrackerFdfcDetail>>(`/tracker-fdfc/${id}/nsdc/refresh`, undefined, {
      timeout: 45_000,
    });
    return res.data.data;
  },

  async resetDispatch(id: string): Promise<TrackerFdfcDetail> {
    const res = await api.post<DataResponse<TrackerFdfcDetail>>(`/tracker-fdfc/${id}/dispatch/reset`);
    return res.data.data;
  },

  /** The team's correction of the courier address; webhook updates stop changing it. */
  async updateAddress(id: string, body: TrackerFdfcAddressInput): Promise<void> {
    await api.patch(`/tracker-fdfc/${id}/address`, body);
  },

  /**
   * A call: new status and/or remark. Leave `status` out to add a remark against
   * the status as it stands. Choosing the status already shown, with no remark,
   * is a no-op server-side.
   */
  async logCall(id: string, body: TrackerFdfcCallLogInput): Promise<void> {
    await api.post(`/tracker-fdfc/${id}/call-log`, body);
  },

  /** What the person said about the kit, as entered by the team. */
  async recordSurvey(id: string, body: TrackerFdfcSurveyInput): Promise<void> {
    await api.post(`/tracker-fdfc/${id}/survey`, body);
  },

  async downloadTemplate(): Promise<void> {
    await downloadXlsx(
      api.get('/tracker-fdfc/import/template', { responseType: 'blob' }),
      'fdfc-tracker-template.xlsx',
    );
  },
};
